import * as Api from '@ViewModels';
import { FilterOperator, InsurancePolicyFilterProperty } from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePoliciesReportingChartsQuery } from '../../../../../queries/Policies/usePoliciesReportingCharts';
import { Alert, AlertDescription } from '../../../../components-v2/Alert';
import { Skeleton } from '../../../../components-v2/Skeleton';
import { THEME } from '../../../../components-v2/Theme/theme';
import { formatCurrency } from '../../helpers/formatCurrency';
import { formatCurrencyAxisLabel } from '../../helpers/formatCurrencyAxisLabel';
import { styleSheet } from './styles';

const CHART_KEY = 'newBusinessByOrigination';
const MONTHS_SHOWN = 24;

const charts: Api.IInsurancePolicyChartConfiguration[] = [
	{
		key: CHART_KEY,
		title: 'New business by origination month',
		groupBy: 'OriginationDate',
		aggregation: 'Sum',
		aggregateField: 'premium',
		dateGranularity: 'Month',
		sortField: 'Label',
		sortAscending: true,
		visualizationType: 'Bar',
	},
];

function OriginationTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: { month: string; count: number; value: number } }>;
}) {
	const point = payload?.[0]?.payload;
	if (!active || !point) {
		return null;
	}
	return (
		<div className={css(styleSheet.tooltip)}>
			<div className={css(styleSheet.tooltipTitle)}>{point.month}</div>
			<p className={css(styleSheet.tooltipLine)}>{formatCurrency(point.value)} premium</p>
			<p className={css(styleSheet.tooltipLine)}>{point.count.toLocaleString('en-US')} policies</p>
		</div>
	);
}

/**
 * Built from today's active policies, so it reaches back before tracking began but misses business that has
 * since lapsed.
 */
export const NewBusinessByOriginationChart = ({ criteria }: { criteria: Api.IInsurancePolicyFilterCriteria[] }) => {
	const filterRequest = useMemo<Api.IInsurancePolicyFilterRequest>(
		() => ({
			criteria: [
				...criteria,
				{
					property: InsurancePolicyFilterProperty.OriginationDate,
					op: FilterOperator.Gt,
					// Gt is exclusive, so start from the last day of the month before the oldest one shown.
					value: moment()
						.subtract(MONTHS_SHOWN - 1, 'months')
						.startOf('month')
						.subtract(1, 'day')
						.format('YYYY-MM-DD'),
				},
			],
		}),
		[criteria]
	);
	const query = usePoliciesReportingChartsQuery({ enabled: true, filterRequest, charts });

	const data = useMemo(
		() =>
			((query.data?.[CHART_KEY]?.data ?? []) as { label: string; count: number; value: number }[])
				.filter(point => !!point.label)
				.map(point => ({ ...point, month: moment(point.label).format('MMM YYYY') })),
		[query.data]
	);

	return (
		<section className={css(styleSheet.card)}>
			<div>
				<h3 className={css(styleSheet.cardTitle)}>New business by origination month</h3>
				<p className={css(styleSheet.cardSubtitle)}>
					Premium of policies written each month that are still active today. Business that lapsed since isn’t included,
					so earlier months read low.
				</p>
			</div>
			{query.isLoading ? (
				<Skeleton>
					<p className={css(styleSheet.empty)}>Loading new business...</p>
				</Skeleton>
			) : query.isError ? (
				<Alert tone='warning'>
					<AlertDescription>
						New business by origination month couldn’t be loaded. Try again in a moment.
					</AlertDescription>
				</Alert>
			) : data.length === 0 ? (
				<p className={css(styleSheet.empty)}>No policies with an origination date in the last two years.</p>
			) : (
				<ResponsiveContainer width='100%' height={220}>
					<BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
						<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} vertical={false} />
						<XAxis dataKey='month' tick={{ fontSize: THEME.typography.fontSizes.xs, fill: THEME.colors.text.main }} />
						<YAxis
							tickFormatter={formatCurrencyAxisLabel}
							tick={{ fontSize: THEME.typography.fontSizes.xs, fill: THEME.colors.text.main }}
						/>
						<Tooltip content={<OriginationTooltip />} />
						<Bar dataKey='value' name='Premium' fill={THEME.colors.chart.aceGray} radius={[4, 4, 0, 0]} />
					</BarChart>
				</ResponsiveContainer>
			)}
		</section>
	);
};
